import { useState, useEffect } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import { useNavigate } from 'react-router-dom';
import { GoEyeClosed, GoEye } from 'react-icons/go';
import SpinnerIcon from '../../assets/spinner2.svg';
import log from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import api from '../../api/api';

const ChangePassword = ({ isChangePasswordModalOpen, onClose, onPasswordUpdateSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBodyScrollLock(isChangePasswordModalOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { idToken, logout } = useUser();
  const { clearCart } = useCart();

  useEffect(() => {
    setIsAnimating(isChangePasswordModalOpen);
  }, [isChangePasswordModalOpen]);

  useEffect(() => {
    if (confirmPassword || newPassword) {
      validateConfirmPassword();
    } else {
      setConfirmPasswordError('');
    }
  }, [confirmPassword, newPassword]);

  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCurrentPasswordChange = (e) => {
    setCurrentPassword(e.target.value);
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    validateNewPassword(value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const validateNewPassword = (pwd) => {
    const errors = [];

    if (pwd.length < 8) {errors.push(t('passwordrequirement1'));}
    if (pwd.length > 30) {errors.push(t('passwordrequirement2'));}

    if (!/[A-Z]/.test(pwd)) {errors.push(t('passwordrequirement3'));}
    if (!/[a-z]/.test(pwd)) {errors.push(t('passwordrequirement4'));}
    if (!/[0-9]/.test(pwd)) {errors.push(t('passwordrequirement5'));}

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd))
    {errors.push(t('passwordrequirement6'));}

    if (errors.length > 0) {
      setNewPasswordError(t('passwordRequirements', { rules: errors.join(', ') }));
      return false;
    } else {
      setNewPasswordError('');
      return true;
    }
  };

  const validateConfirmPassword = () => {
    if (confirmPassword !== newPassword) {
      setConfirmPasswordError(t('Passwords do not match'));
      return false;
    } else {
      setConfirmPasswordError('');
      return true;
    }
  };

  const isButtonEnabled =
        currentPassword &&
        newPassword &&
        confirmPassword &&
        !newPasswordError &&
        !confirmPasswordError;

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.put(
        '/user/password',
        {
          currentPassword,
          newPassword,
          confirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      log.log('Password update success', response.data);
      onPasswordUpdateSuccess();
      setErrorMsg('');
      onClose();
    } catch (error) {
      log.error('Failed to update password:', error);
      const errError = error.response?.data?.error || '';
      const errMessage = error.response?.data?.message || '';
      if (errMessage === 'Current password is incorrect' || errMessage === 'Invalid format') {
        setCurrentPasswordError(t('Current password is incorrect'));
        setErrorMsg('');
      } 
      else if (errError === 'TOKEN_EXPIRED' || errError === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      }
      else {
        setCurrentPasswordError('');
        setErrorMsg(getApiErrorMessage(error, t));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div
        className={`fixed bottom-0 lg:top-0 right-0 left-auto w-full lg:w-[550px] h-fit lg:h-full max-h-[90dvh] lg:max-h-full overflow-y-auto bg-white z-50 transition-transform duration-300 ${isAnimating ? 'modalanimatedRight' : 'whennotmodalcloseRight'}`}
      >
        {/* Close Icon */}
        <div className='absolute top-6 right-4'>
          <button onClick={onClose}>
            <IoCloseSharp className='text-2xl' />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handlePasswordUpdate}>
          <div className='px-4 mt-10 lg:mt-20'>
            <h2 className='text-3xl lg:text-4xl my-3 font-semibold'>{t('Change Password')}</h2>

            <p className='text-[#918D8D] text-sm'>{t('Please enter your current password down below')}</p>

            <div className={'mt-7 mb-10'}>
              <div className='flex items-center justify-between gap-2 rounded-[10px] min-h-12 px-3 border border-gray-600 w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'>
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder={t('Current password')}
                  className='placeholder:font-light font-medium w-full outline-none h-full flex-1 bg-transparent border-none'
                  value={currentPassword}
                  onChange={handleCurrentPasswordChange}
                />

                {/* Show/Hide Password Icon */}
                <button
                  type="button"
                  className='flex items-center justify-center w-10 h-full'
                  onClick={toggleCurrentPasswordVisibility}
                >
                  {showCurrentPassword ? (
                    <GoEye className='w-6 h-6 text-black opacity-55' />
                  ) : (
                    <GoEyeClosed className='w-6 h-6 text-black opacity-55' />
                  )}
                </button>
              </div>
              {currentPasswordError && <p className='text-red-500 text-sm mt-1'>{currentPasswordError}</p>}
            </div>

            <p className='text-[#918D8D] text-sm'>{t('Please enter your new password down below')}</p>

            <div className='flex items-center justify-between gap-2 rounded-[10px] min-h-12 px-3 mt-7 border border-gray-600 w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'>
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder={t('New password')}
                className='placeholder:font-light font-medium w-full outline-none h-full flex-1 bg-transparent border-none'
                value={newPassword}
                onChange={handleNewPasswordChange}
              />

              {/* Show/Hide Password Icon */}
              <button
                type="button"
                className='flex items-center justify-center w-10 h-full'
                onClick={toggleNewPasswordVisibility}
              >
                {showNewPassword ? (
                  <GoEye className='w-6 h-6 text-black opacity-55' />
                ) : (
                  <GoEyeClosed className='w-6 h-6 text-black opacity-55' />
                )}
              </button>
            </div>
            {newPasswordError && <p className='text-red-500 text-sm mt-1'>{newPasswordError}</p>}

            <div className='flex items-center justify-between gap-2 rounded-[10px] min-h-12 px-3 mt-5 border border-gray-600 w-full outline-none transition-all duration-300 hover:border-black focus:border-2 focus:border-[#1976d2] focus:ring-0'>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('Confirm password')}
                className='placeholder:font-light font-medium w-full outline-none h-full flex-1 bg-transparent border-none'
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
              />

              {/* Show/Hide Password Icon */}
              <button
                type="button"
                className='flex items-center justify-center w-10 h-full'
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? (
                  <GoEye className='w-6 h-6 text-black opacity-55' />
                ) : (
                  <GoEyeClosed className='w-6 h-6 text-black opacity-55' />
                )}
              </button>
            </div>
            {confirmPasswordError && <p className='text-red-500 text-sm mt-1'>{confirmPasswordError}</p>}

            <button
              type="submit"
              className={`h-[52px] px-4 text-center bg-[#0b1223] text-white text-base capitalize rounded-lg my-10 w-full font-medium transition-opacity duration-300 ${
                isButtonEnabled ? 'opacity-100' : 'opacity-60'
              }`}
              disabled={!isButtonEnabled || isLoading}
            >
              {isLoading ? (
                <img
                  src={SpinnerIcon}
                  alt={t('loggingin')}
                  className="h-5 w-5 animate-spin mx-auto"
                />
              ) : (
                t('Update')
              )}
            </button>

            {errorMsg && <p className="text-red-500 text-sm mt-[-20px] mb-4">{t(errorMsg)}</p>}
          </div>
        </form>

      </div>

      {/* Overlay */}
      {isChangePasswordModalOpen && (
        <div
          className='fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-40'
          onClick={onClose}
        />
      )}
    </div>
  );
};

export default ChangePassword;
